import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API, getAuthHeader } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { SearchableSelect } from './ui/searchable-select';
import { ChevronDown, ChevronRight, FileDown, FileSpreadsheet, FileText, Loader2, Search, AlertTriangle } from 'lucide-react';
import { fmtMoney, periodTotals } from './lease-history/leaseHistoryConfig';
import { exportExcel, exportPdf } from './lease-history/leaseHistoryExport';
import { LeaseHistoryMonthDetail } from './lease-history/LeaseHistoryMonthDetail';

const todayIso = () => new Date().toISOString().slice(0, 10);
const firstDayOfYearIso = () => `${new Date().getFullYear()}-01-01`;

const TONES = { slate: 'text-slate-800', indigo: 'text-indigo-700', blue: 'text-blue-700', red: 'text-red-700', amber: 'text-amber-700' };

const Kpi = ({ label, value, sub, tone = 'slate', testId }) => (
  <div className="rounded-xl border border-slate-200 bg-white px-4 py-3" data-testid={testId}>
    <div className="text-[11px] font-medium uppercase tracking-wide text-slate-500">{label}</div>
    <div className={`mt-1 text-2xl font-semibold ${TONES[tone]}`}>{value}</div>
    {sub && <div className="mt-0.5 text-xs text-slate-500">{sub}</div>}
  </div>
);

const Money = ({ count, amount }) => (
  <div className="leading-tight">
    <div className="font-medium text-slate-800">{count}</div>
    <div className="text-[11px] text-slate-500">AED {fmtMoney(amount)}</div>
  </div>
);

const SUMMARY_COLS = ['', 'Month', 'In possession', 'Start', '+ HO', '- TO', 'End', 'Fines', 'SALIK', 'Maintenance', 'Download'];

const MonthRow = ({ m, open, onToggle, onPdf, onExcel }) => {
  const c = m.counts;
  return (
    <>
      <tr className={`cursor-pointer border-t border-slate-100 text-sm transition-colors hover:bg-green-50/40 ${open ? 'bg-green-50/60' : ''}`} onClick={onToggle} data-testid={`month-row-${m.month_key}`}>
        <td className="pl-3">{open ? <ChevronDown className="w-4 h-4 text-green-700" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}</td>
        <td className="py-3 font-semibold text-slate-800 whitespace-nowrap">{m.month_label}</td>
        <td className="py-3" data-testid={`month-possession-${m.month_key}`}>
          <span className="inline-flex min-w-[2.5rem] justify-center rounded-md bg-indigo-600 px-2 py-0.5 text-sm font-bold text-white">{c.possession ?? 0}</span>
          {c.possession_swap_only > 0 && (
            <span className="ml-2 inline-flex items-center gap-1 text-[11px] text-amber-700" title="Bikes used in battery swaps with no handover form">
              <AlertTriangle className="w-3 h-3" />{c.possession_swap_only} no form
            </span>
          )}
        </td>
        <td className="py-3 text-slate-600">{c.opening_assigned}</td>
        <td className="py-3 font-medium text-emerald-700">+{c.handovers}</td>
        <td className="py-3 font-medium text-orange-700">{c.takeovers ? `-${c.takeovers}` : 0}</td>
        <td className="py-3 font-semibold text-blue-700" data-testid={`month-end-${m.month_key}`}>{c.motorcycles_assigned}</td>
        <td className="py-3"><Money count={c.fines} amount={c.fines_amount} /></td>
        <td className="py-3"><Money count={c.salik} amount={c.salik_amount} /></td>
        <td className="py-3"><Money count={c.maintenance} amount={c.maintenance_amount} /></td>
        <td className="py-3 pr-3" onClick={(e) => e.stopPropagation()}>
          <div className="flex gap-1">
            <Button size="sm" variant="ghost" className="h-8 px-2" onClick={onPdf} data-testid={`month-pdf-${m.month_key}`} title="Download month PDF"><FileText className="w-4 h-4 text-red-600" /></Button>
            <Button size="sm" variant="ghost" className="h-8 px-2" onClick={onExcel} data-testid={`month-excel-${m.month_key}`} title="Download month Excel"><FileSpreadsheet className="w-4 h-4 text-green-700" /></Button>
          </div>
        </td>
      </tr>
      {open && (
        <tr className="bg-slate-50/70">
          <td colSpan={SUMMARY_COLS.length} className="px-4 pb-5 pt-3"><LeaseHistoryMonthDetail m={m} /></td>
        </tr>
      )}
    </>
  );
};

const TotalsRow = ({ t }) => (
  <tr className="border-t-2 border-slate-300 bg-slate-100 text-sm font-semibold" data-testid="lease-history-totals-row">
    <td />
    <td className="py-3">Period total</td>
    <td className="py-3 text-indigo-700">{t.possession} <span className="text-[11px] font-normal text-slate-500">distinct bikes</span></td>
    <td className="py-3">{t.opening}</td>
    <td className="py-3 text-emerald-700">+{t.handovers}</td>
    <td className="py-3 text-orange-700">-{t.takeovers}</td>
    <td className="py-3 text-blue-700">{t.closing}</td>
    <td className="py-3"><Money count={t.fines} amount={t.finesAmount} /></td>
    <td className="py-3"><Money count={t.salik} amount={t.salikAmount} /></td>
    <td className="py-3"><Money count={t.maintenance} amount={t.maintenanceAmount} /></td>
    <td />
  </tr>
);

const ReportView = ({ report, expanded, toggle }) => {
  const months = report.months;
  const t = useMemo(() => periodTotals(months), [months]);
  const label = `${report.date_range.start}_to_${report.date_range.end}`;
  const run = (fn, what) => { try { fn(); toast.success(`Downloaded ${what}`); } catch (e) { toast.error(`Export failed: ${e.message}`); } };
  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-lg font-semibold text-slate-900" data-testid="lease-history-lessee-name">{report.lessee.name}</div>
          <div className="text-sm text-slate-500">{report.date_range.start} to {report.date_range.end} - {months.length} month{months.length !== 1 ? 's' : ''}</div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => run(() => exportPdf(report, months, label), 'full report (PDF)')} data-testid="lease-history-full-pdf"><FileText className="w-4 h-4 mr-2 text-red-600" />Full report PDF</Button>
          <Button variant="outline" onClick={() => run(() => exportExcel(report, months, label), 'full report (Excel)')} data-testid="lease-history-full-excel"><FileSpreadsheet className="w-4 h-4 mr-2 text-green-700" />Full report Excel</Button>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        <Kpi label="Bikes in possession" value={t.possession} sub="distinct bikes in period" tone="indigo" testId="kpi-possession" />
        <Kpi label="Assigned now (period end)" value={t.closing} sub={`started with ${t.opening}`} tone="blue" testId="kpi-closing" />
        <Kpi label="Handovers / Takeovers" value={`+${t.handovers} / -${t.takeovers}`} sub="permanent takeovers only" testId="kpi-moves" />
        <Kpi label="Fines + SALIK" value={`AED ${fmtMoney(t.finesAmount + t.salikAmount)}`} sub={`${t.fines} fines, ${t.salik} SALIK`} tone="red" testId="kpi-charges" />
        <Kpi label="Maintenance" value={`AED ${fmtMoney(t.maintenanceAmount)}`} sub={`${t.maintenance} jobs`} tone="amber" testId="kpi-maintenance" />
      </div>
      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-left" data-testid="lease-history-summary-table">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
            <tr>{SUMMARY_COLS.map((h) => <th key={h} className="py-2.5 font-semibold first:pl-3">{h}</th>)}</tr>
          </thead>
          <tbody>
            {months.map((m) => (
              <MonthRow key={m.month_key} m={m} open={!!expanded[m.month_key]} onToggle={() => toggle(m.month_key)}
                onPdf={() => run(() => exportPdf(report, [m], m.month_key), `${m.month_label} (PDF)`)}
                onExcel={() => run(() => exportExcel(report, [m], m.month_key), `${m.month_label} (Excel)`)} />
            ))}
            {months.length > 1 && <TotalsRow t={t} />}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-slate-500">Start + HO - TO = End. Only permanent takeovers (or a move to another lessee) reduce the count. "In possession" counts every bike the lessee had at any time in the month, including bikes seen only in their riders' battery swaps. Click a month to see the details.</p>
    </div>
  );
};

const LeaseHistoryReport = () => {
  const [lessees, setLessees] = useState([]);
  const [lesseeId, setLesseeId] = useState('');
  const [startDate, setStartDate] = useState(firstDayOfYearIso());
  const [endDate, setEndDate] = useState(todayIso());
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    axios.get(`${API}/lessees`, { headers: getAuthHeader() })
      .then((resp) => setLessees(Array.isArray(resp.data) ? resp.data : []))
      .catch(() => toast.error('Failed to load lessees'));
  }, []);

  const lesseeOptions = useMemo(
    () => lessees.map((l) => ({ value: l.id, label: l.company_name || l.lessee_name || '(unnamed)' })),
    [lessees]
  );

  const fetchReport = async () => {
    if (!lesseeId) return toast.error('Please select a lessee');
    if (!startDate || !endDate) return toast.error('Please select both start and end dates');
    if (startDate > endDate) return toast.error('Start date must be on or before end date');
    setLoading(true);
    try {
      const resp = await axios.get(`${API}/motorcycles/lease-history`, {
        params: { lessee_id: lesseeId, start_date: startDate, end_date: endDate },
        headers: getAuthHeader(),
        timeout: 90000,
      });
      setReport(resp.data);
      setExpanded({});
      if (!resp.data?.months?.length) toast.info('No data found for the selected range');
    } catch (error) {
      const detail = error?.response?.data?.detail || error?.message || 'Unknown error';
      toast.error(`Failed to load lease history (${error?.response?.status || 'network'}): ${detail}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="border-0 shadow-lg" data-testid="lease-history-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><FileDown className="w-5 h-5 text-green-600" />Lease History</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="mb-6 grid grid-cols-1 gap-4 rounded-xl border border-slate-200 bg-slate-50/60 p-4 md:grid-cols-4">
          <div data-testid="lease-history-lessee-field">
            <Label>Lessee</Label>
            <SearchableSelect value={lesseeId} onValueChange={setLesseeId} options={lesseeOptions} placeholder="Select lessee..." data-testid="lease-history-lessee-select" />
          </div>
          <div>
            <Label>Start Date</Label>
            <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} data-testid="lease-history-start-date" />
          </div>
          <div>
            <Label>End Date</Label>
            <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} data-testid="lease-history-end-date" />
          </div>
          <div className="flex items-end">
            <Button onClick={fetchReport} disabled={loading} className="w-full bg-green-600 text-white hover:bg-green-700" data-testid="lease-history-generate-btn">
              {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Loading...</> : <><Search className="w-4 h-4 mr-2" />Generate Report</>}
            </Button>
          </div>
        </div>
        {report && (report.months.length === 0
          ? <div className="py-10 text-center text-slate-400">No activity in the selected date range.</div>
          : <ReportView report={report} expanded={expanded} toggle={(k) => setExpanded((p) => ({ ...p, [k]: !p[k] }))} />)}
      </CardContent>
    </Card>
  );
};

export default LeaseHistoryReport;
